import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type {
  ChatDetail, ChatMessage, ChatPerson, ChatPersonStatus, ChatReaction, ChatSummary, ChatThreadSummary,
} from "@/lib/types";
import { stateText, type AvailabilityTone } from "./acd.js";

/**
 * Messages between the people of a tenant (platform ADR-186): a team's chat and direct ones. The
 * rules the Team Chat page and the toasts lean on, kept here so they can be tested apart from React.
 */

/** A chat with a person or a team, rather than a contact's conversation (platform ADR-189). */
export function isPeopleChat(chat: ChatSummary): boolean {
  return chat.kind === "team" || chat.kind === "direct";
}

/**
 * The chat window's list: teams and direct messages, with any contact's conversation that is going
 * on (`isLiveContactChat`), first; then the rest of the contacts' conversations, which the window
 * keeps folded until asked for. Each in the order core-api lists chats: newest first. An admin's
 * other teams (platform ADR-207) are left to the Team Chat page.
 */
export function windowChats(chats: readonly ChatSummary[] | undefined): {
  people: ChatSummary[];
  contacts: ChatSummary[];
} {
  const all = (chats ?? []).filter((c) => !c.other_team);
  const mine = new Set(all.map((c) => c.id));
  const shown = (c: ChatSummary) => isPeopleChat(c) || isLiveContactChat(c, mine);
  return { people: all.filter(shown), contacts: all.filter((c) => !shown(c)) };
}

/** Unread messages across every chat: the sidebar's and the rail's count. */
export function unreadTotal(chats: readonly ChatSummary[] | undefined): number {
  return (chats ?? []).reduce((n, c) => n + (c.archived ? 0 : c.unread), 0);
}

/**
 * A chat marked read, in the cached chats list under `queryKey`: its own messages are read at once,
 * and its threads' unread replies stay until each thread is opened (`thread_unread`, platform
 * ADR-191; the server counts it the same way). Then the list is read again, so the server's count
 * stands: a message or reply that came in after the cached list was read is not left off the badge.
 */
export function markReadInList(client: QueryClient, queryKey: QueryKey, chatId: number): void {
  client.setQueryData<ChatSummary[]>(queryKey, (old) =>
    old?.map((c) => (c.id === chatId ? { ...c, unread: c.thread_unread ?? 0 } : c)),
  );
  void client.invalidateQueries({ queryKey, exact: true });
}

/** "Today", "Yesterday", or the date, in the reader's own time. */
export function dayLabel(at: string, now: Date = new Date()): string {
  const d = new Date(at);
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((start(now) - start(d)) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

/** One message on the page, and whether it starts a run: a new day, a new sender, or more than
 *  five minutes after the one before. A run shows its sender's name once. */
export interface MessageRow {
  message: ChatMessage;
  day: string | null;
  startsRun: boolean;
}

const RUN_GAP_MS = 5 * 60_000;

export function messageRows(messages: readonly ChatMessage[], now: Date = new Date()): MessageRow[] {
  let lastDay: string | null = null;
  let prev: ChatMessage | null = null;
  return messages.map((message) => {
    const day = dayLabel(message.created_at, now);
    const newDay = day !== lastDay;
    const startsRun =
      newDay ||
      prev === null ||
      prev.sender_id !== message.sender_id ||
      new Date(message.created_at).getTime() - new Date(prev.created_at).getTime() > RUN_GAP_MS;
    lastDay = day;
    prev = message;
    return { message, day: newDay ? day : null, startsRun };
  });
}

/** Who else has read up to this message, by name, for "Seen by" under the sender's last one. */
export function seenBy(chat: ChatDetail | undefined, me: number, messageId: number): string[] {
  if (!chat) return [];
  return chat.members
    .filter((m) => m.user_id !== me && m.last_read_id !== null && m.last_read_id >= messageId)
    .map((m) => m.name ?? "Someone");
}

/** "Seen", "Seen by Grace", "Seen by 3": short enough for under a message. */
export function seenText(names: readonly string[], kind: ChatSummary["kind"]): string | null {
  if (names.length === 0) return null;
  if (kind === "direct") return "Seen";
  if (names.length <= 2) return `Seen by ${names.join(" and ")}`;
  return `Seen by ${names.length}`;
}

/** The chats with a message from somebody else that was not there the last time the list was
 *  read, and is not in the chat on screen: what a toast says arrived. In a chat the reader is a
 *  guest in, only a message that mentions them. */
export function arrivals(
  before: readonly ChatSummary[] | undefined,
  after: readonly ChatSummary[],
  me: number,
  openChatId: number | null,
): ChatSummary[] {
  if (before === undefined) return [];
  const seen = new Map(before.map((c) => [c.id, c.last_message?.id ?? 0]));
  return after.filter((c) => {
    const last = c.last_message;
    if (!last || last.sender_id === me || c.id === openChatId || c.unread === 0) return false;
    // A guest's open team chat (platform ADR-190) counts its unread, and pops only a mention.
    if (c.guest && !last.mentions_me) return false;
    return last.id > (seen.get(c.id) ?? 0);
  });
}

/** The chats with a reply from somebody else, in a thread the reader follows, that was not there
 *  the last time the list was read and is not in the thread on screen (platform ADR-191): what a
 *  toast says came in a thread. */
export function replyArrivals(
  before: readonly ChatSummary[] | undefined,
  after: readonly ChatSummary[],
  me: number,
  openThreadId: number | null,
): ChatSummary[] {
  if (before === undefined) return [];
  const seen = new Map(before.map((c) => [c.id, c.last_reply?.id ?? 0]));
  return after.filter((c) => {
    const reply = c.last_reply;
    if (!reply || reply.sender_id === me || reply.thread_id === openThreadId || !c.thread_unread) return false;
    return reply.id > (seen.get(c.id) ?? 0);
  });
}

/** The line under a message that has a thread: "1 reply" or "3 replies", and when the last came,
 *  in the reader's own time: today's by the clock, an older one by its day. */
export function threadLine(thread: Pick<ChatThreadSummary, "reply_count" | "last_reply_at">, now: Date = new Date()): string {
  const count = `${thread.reply_count} ${thread.reply_count === 1 ? "reply" : "replies"}`;
  if (!thread.last_reply_at) return count;
  const at = new Date(thread.last_reply_at);
  const when =
    dayLabel(thread.last_reply_at, now) === "Today"
      ? at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
      : dayLabel(thread.last_reply_at, now);
  return `${count} · last ${when}`;
}

/** What a message says in a list or a toast: its words, that it shares a contact, or that it asks
 *  for a closer (platform ADR-187). */
export function previewText(last: ChatSummary["last_message"], me: number): string {
  if (!last) return "No messages yet";
  if (last.handoff) return askPreview(last.handoff, last.sender_name);
  const words = last.preview || (last.has_contact ? "Shared a contact" : "");
  return last.sender_id === me ? `You: ${words}` : words;
}

/** An ask for a closer in a list or a toast: who asked whom, and where it stands for the reader.
 *  "You asked Closers for a closer · waiting", "Sam Ortega needs a closer · Carlos Vega took it". */
function askPreview(ask: NonNullable<NonNullable<ChatSummary["last_message"]>["handoff"]>, sender: string | null): string {
  const of = ask.team_name ?? ask.asked_user_name ?? "a closer";
  const head = ask.mine ? `You asked ${of} for a closer` : `${ask.asked_by_name ?? sender ?? "A rep"} needs a closer`;
  const taker = ask.took ? "you" : (ask.taken_by_name ?? "a closer");
  const now = {
    waiting: "waiting",
    taken: `${ask.took ? "you" : taker} took it`,
    transferred: `sent to ${taker}`,
    cancelled: "called off",
    expired: ask.taken_by === null ? "nobody took it" : "the transfer did not come",
    ended: "the call ended",
  }[ask.state];
  return `${head} · ${now}`;
}

/** A contact's conversation that is going on (platform ADR-189): something in it is unread, or its
 *  newest message is an ask still waiting or taken that went to a chat the reader is not in (`mine`:
 *  the ids of the reader's chats). An ask to an open team (platform ADR-190) puts the setter in the
 *  team's chat, where the card is already in front of them. The chat window lists these with the
 *  teams and direct messages. */
export function isLiveContactChat(chat: ChatSummary, mine: ReadonlySet<number>): boolean {
  if (chat.kind !== "contact") return false;
  if (chat.unread > 0) return true;
  const ask = chat.last_message?.handoff;
  return (ask?.state === "waiting" || ask?.state === "taken") && !mine.has(ask.chat_id);
}

/** Whether a person's card in a chat offers Message (a direct chat with them): not for oneself,
 *  not for somebody deactivated, and not in the direct chat already with them. */
export function messageable(
  person: Pick<ChatPerson, "user_id" | "is_active">, me: number, chat: Pick<ChatSummary, "kind">,
): boolean {
  return person.user_id !== me && person.is_active && chat.kind !== "direct";
}

/** Whether the person may close a chat (platform ADR-186, "Closing a chat"): a direct chat or a
 *  contact's conversation, which then stays off their list until something new comes in it. A
 *  team's chat is not closed: its people are in it while they are in the team, and a guest leaves. */
export function closable(chat: Pick<ChatSummary, "kind">): boolean {
  return chat.kind === "direct" || chat.kind === "contact";
}

// --- reactions (platform ADR-186, "Reactions") -------------------------------------------------

/** The emoji the hover bar offers at once. */
export const QUICK_REACTIONS: readonly string[] = ["👍", "❤️", "😂", "🎉", "👀", "✅", "🙏", "🔥"];

/** The rest the picker offers. core-api takes any emoji; these are the ones a chat at work uses. */
export const MORE_REACTIONS: readonly string[] = [
  "😀", "😄", "😅", "🙂", "😉", "😊", "😍", "🤔",
  "😮", "😬", "😢", "😭", "😡", "🙌", "👏", "💪",
  "👌", "✌️", "🤞", "👋", "🙋", "💯", "⭐", "⚡",
  "📞", "📅", "⏰", "💼", "🚀", "🏆", "❌", "⚠️",
];

/** A message's reactions once the reader puts one emoji on (`on`) or takes theirs back, as the
 *  screen shows them before core-api answers: the order kept, a new emoji last, and an emoji gone
 *  once nobody is left on it. `myName` is how the reader is named among the others. */
export function toggleReaction(
  list: readonly ChatReaction[], emoji: string, on: boolean, myName: string,
): ChatReaction[] {
  const at = list.findIndex((r) => r.emoji === emoji);
  const found = at >= 0 ? list[at] : undefined;
  if (on) {
    if (!found) return [...list, { emoji, count: 1, mine: true, names: [myName] }];
    if (found.mine) return [...list];
    return list.map((r, i) => (i === at ? { ...r, count: r.count + 1, mine: true, names: [...r.names, myName] } : r));
  }
  if (!found || !found.mine) return [...list];
  if (found.count <= 1) return list.filter((_, i) => i !== at);
  const names = [...found.names];
  const mineAt = names.indexOf(myName);
  if (mineAt >= 0) names.splice(mineAt, 1);
  return list.map((r, i) => (i === at ? { ...r, count: r.count - 1, mine: false, names } : r));
}

/** Who reacted, as the reaction's tooltip says it: "You and Carlos Vega", "Ana, Ben and 3 others".
 *  The reader is "You", first. */
export function reactionWho(reaction: ChatReaction, myName: string | null): string {
  const names = [...reaction.names];
  if (reaction.mine) {
    const at = myName ? names.indexOf(myName) : -1;
    if (at >= 0) names.splice(at, 1);
    names.unshift("You");
  }
  const shown = names.slice(0, reaction.count);
  const others = reaction.count - shown.length;
  const all = others > 0 ? [...shown, `${others} ${others === 1 ? "other" : "others"}`] : shown;
  if (all.length <= 1) return all[0] ?? "";
  return `${all.slice(0, -1).join(", ")} and ${all.at(-1)}`;
}

// --- mentions (platform ADR-186, "Mentions") ---------------------------------------------------

/** Somebody a message mentions, as the composer keeps them: who, and the name typed after "@". */
export interface Mention {
  user_id: number;
  name: string;
}

/** The "@..." being typed just before the caret: where its "@" is, and what follows it. An "@"
 *  counts at the start or after a space or a new line, and what follows it is no more than 40
 *  characters on one line. Null when the caret is not in one. */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const hit = /(^|\s)@([^@\n]{0,40})$/.exec(before);
  if (!hit) return null;
  const query = hit[2] ?? "";
  return { start: before.length - query.length - 1, query };
}

/** Whom an "@" offers for what follows it: the chat's other active people whose name holds it,
 *  the names that start with it first, at most `most`. */
export function mentionMatches(
  people: readonly Pick<ChatPerson, "user_id" | "name" | "is_active">[], query: string, me: number, most = 6,
): Mention[] {
  const q = query.trim().toLowerCase();
  const named = people
    .filter((p) => p.user_id !== me && p.is_active && p.name)
    .map((p) => ({ user_id: p.user_id, name: p.name as string }));
  const hits = named.filter((p) => p.name.toLowerCase().includes(q));
  const starts = (m: Mention) => (m.name.toLowerCase().startsWith(q) ? 0 : 1);
  return hits.sort((a, b) => starts(a) - starts(b) || a.name.localeCompare(b.name)).slice(0, most);
}

/** The words with the "@query" from `start` to `caret` made "@Name ", and where the caret goes. */
export function insertMention(text: string, start: number, caret: number, name: string): { text: string; caret: number } {
  const said = `@${name} `;
  return { text: text.slice(0, start) + said + text.slice(caret), caret: start + said.length };
}

/** The people picked in the composer whose "@Name" is still in the words being sent. */
export function mentionIdsIn(body: string, picked: readonly Mention[]): number[] {
  return [...new Set(picked.filter((m) => body.includes(`@${m.name}`)).map((m) => m.user_id))];
}

/** A message's words as plain runs and the "@Name" of each person it mentions, in order, so the
 *  page can mark them. The longest name is tried first, so "@Ana Maria" is not read as "@Ana". */
export function mentionParts(
  body: string, mentions: readonly { user_id: number; name: string | null }[],
): { text: string; mention?: Mention }[] {
  const named = mentions
    .filter((m) => m.name)
    .map((m) => ({ user_id: m.user_id, name: m.name as string }))
    .sort((a, b) => b.name.length - a.name.length);
  if (named.length === 0) return [{ text: body }];
  const escaped = named.map((m) => m.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const parts: { text: string; mention?: Mention }[] = [];
  let at = 0;
  for (const hit of body.matchAll(new RegExp(`@(${escaped.join("|")})`, "g"))) {
    const index = hit.index ?? 0;
    if (index > at) parts.push({ text: body.slice(at, index) });
    parts.push({ text: hit[0], mention: named.find((m) => m.name === hit[1]) });
    at = index + hit[0].length;
  }
  if (at < body.length) parts.push({ text: body.slice(at) });
  return parts;
}

/** Somebody's phone status as their card says it (platform ADR-186, "Status"): the state in the
 *  console's own words, as long as it has lasted, and its dot's tone. */
export function statusLine(status: ChatPersonStatus, now: number = Date.now()): { label: string; tone: AvailabilityTone } {
  if (status.state === "no_phone") return { label: "No phone", tone: "neutral" };
  if (status.state === "signed_out") {
    return { label: status.phone === false ? "Not taking calls · phone off" : "Not taking calls", tone: "neutral" };
  }
  const { label, tone } = stateText(status.state, status.code);
  const since = status.since ? Date.parse(status.since) : Number.NaN;
  const minutes = Number.isFinite(since) ? Math.floor((now - since) / 60_000) : 0;
  if (minutes < 1) return { label, tone };
  const lasted = minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h${minutes % 60 ? ` ${minutes % 60} min` : ""}`;
  return { label: `${label} · ${lasted}`, tone };
}

/** A fresh id for a message this console sends, so a retry is not a second message. */
export function newClientId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  return c?.randomUUID ? c.randomUUID() : `m-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
